import { ProposalData } from './types'

function daysUntil(dateStr: string): number {
  const target = new Date(dateStr)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  target.setHours(0, 0, 0, 0)
  return Math.max(
    0,
    Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
  )
}

export const existingProposalFixture: ProposalData = {
  header: {
    title: "NIH R01: Neural Mechanisms of Alzheimer's Disease Progression",
    funder: 'NIH / NIA',
    mechanism: 'R01',
    foaNumber: 'PA-25-102',
    piName: 'Dr. Sarah Chen',
    proposalStatus: 'In Progress',
    overallProgress: 47,
    daysLeft: daysUntil('2026-03-05'),
    deadline: 'Mar 5, 2026',
    team: [
      {
        initials: 'SC',
        name: 'Dr. Sarah Chen',
        institution: 'UC San Francisco',
        color: 'bg-indigo-100 text-indigo-700',
      },
      {
        initials: 'MS',
        name: 'Maria Santos',
        institution: 'UC San Francisco',
        color: 'bg-emerald-100 text-emerald-700',
      },
      {
        initials: 'JP',
        name: 'Dr. James Park',
        institution: 'University of Michigan',
        color: 'bg-amber-100 text-amber-700',
      },
    ],
    riskStatus: 'at-risk',
    riskSummary: `At risk due to 1 overdue milestone, 2 overdue tasks, and 2 unsatisfied criteria with only ${daysUntil('2026-03-05')} days remaining. 9 of 19 artifacts complete.`,
    riskSignals: [
      {
        id: 'timeline',
        label: 'Timeline',
        iconName: 'Clock',
        status: 'at-risk',
        summary: '1 milestone overdue (Budget draft to SPO)',
        detail: `${daysUntil('2026-03-05')} days left — 9 of 19 artifacts complete`,
      },
      {
        id: 'artifacts',
        label: 'Artifacts',
        iconName: 'FileBox',
        status: 'needs-attention',
        summary: '9 of 19 artifacts complete',
        detail: '2 not satisfied · 2 unknown',
      },
      {
        id: 'criteria',
        label: 'Criteria',
        iconName: 'Lightbulb',
        status: 'needs-attention',
        summary: '2 not satisfied, 1 unknown of 13 criteria',
        detail: 'Innovation overlap · Letters of support incomplete',
      },
      {
        id: 'tasks',
        label: 'Tasks',
        iconName: 'CheckSquare',
        status: 'at-risk',
        summary: '2 overdue tasks',
        detail: 'Letter of support (Jan 3) · UMich F&A rate (Jan 5)',
      },
    ],
  },
  milestones: [
    {
      id: '1',
      title: 'Internal LOI submitted',
      date: 'Jan 3, 2027',
      status: 'completed',
      type: 'internal',
      source: 'manual',
    },
    {
      id: '2',
      title: 'Specific Aims finalized',
      date: 'Jan 8, 2027',
      status: 'completed',
      type: 'internal',
      source: 'manual',
      note: 'Approved by all co-investigators',
    },
    {
      id: '3',
      title: 'Budget draft to SPO',
      date: 'Jan 12, 2027',
      status: 'overdue',
      type: 'internal',
      source: 'ai-extracted',
      sourceRef: 'SF424 R&R Application Guide, Section II.5',
      note: 'Awaiting subcontract quotes from UMich',
    },
    {
      id: '4',
      title: 'Research Strategy first draft',
      date: 'Jan 18, 2027',
      status: 'upcoming',
      type: 'internal',
      source: 'manual',
    },
    {
      id: '5',
      title: 'Internal review deadline',
      date: 'Jan 22, 2027',
      status: 'upcoming',
      type: 'internal',
      source: 'policy',
      sourceRef: 'University Sponsored Programs Policy, Section 3.2',
      note: 'Department review committee',
    },
    {
      id: '6',
      title: 'SPO final review',
      date: 'Jan 28, 2027',
      status: 'upcoming',
      type: 'internal',
      source: 'policy',
      sourceRef: 'Institutional Submission Policy, 5 business days prior',
    },
    {
      id: '7',
      title: 'Grants.gov submission',
      date: 'Feb 3, 2027',
      status: 'upcoming',
      type: 'external',
      source: 'ai-extracted',
      sourceRef: 'Grants.gov Application Guide, Submission Requirements',
      note: '2 business days before NIH receipt',
    },
    {
      id: '8',
      title: 'NIH receipt date',
      date: 'Feb 5, 2027',
      status: 'upcoming',
      type: 'external',
      source: 'ai-extracted',
      sourceRef: 'NIH Notice NOT-OD-23-104, Standard Receipt Dates',
      note: 'Standard receipt date for new R01 applications',
    },
  ],
  criteria: [
    {
      id: '1',
      title:
        'PI must hold a faculty appointment at an accredited US institution',
      type: 'Eligibility',
      sourceRef: 'PA-25-102, Section III — Eligibility Information',
      artifact: 'Overall',
      status: 'satisfied',
      notes:
        'PI Dr. Sarah Chen holds tenured faculty appointment at an accredited research university. Institutional verification confirmed.',
    },
    {
      id: '2',
      title:
        'Applicant organization must have active Federalwide Assurance (FWA)',
      type: 'Eligibility',
      sourceRef: 'PA-25-102, Section III — Organizational Eligibility',
      artifact: 'Overall',
      status: 'satisfied',
      notes: 'Institution FWA is active and on file with OHRP. Expires 2029.',
    },
    {
      id: '3',
      title: 'No clinical trials allowed under this FOA',
      type: 'Eligibility',
      sourceRef: 'PA-25-102, Section I — Funding Opportunity Description',
      artifact: 'Research Strategy',
      status: 'satisfied',
      notes:
        'Research Strategy describes basic science approach only. No clinical intervention, treatment, or patient enrollment proposed.',
    },
    {
      id: '4',
      title: "Must address Alzheimer's disease mechanisms directly",
      type: 'Explicit',
      sourceRef: 'PA-25-102, Section I — Scientific Scope',
      artifact: 'Specific Aims',
      status: 'satisfied',
      notes:
        'All three specific aims directly address AD pathology mechanisms including tau propagation and neuroinflammation.',
    },
    {
      id: '5',
      title:
        'Use NIA-recommended experimental methodologies for biomarker studies',
      type: 'Explicit',
      sourceRef: 'PA-25-102, Section IV — Application Components',
      artifact: 'Research Strategy',
      status: 'satisfied',
      notes:
        'Proposed methods align with NIA-recommended approaches. Includes validated CSF biomarker assays and standardized MRI protocols.',
    },
    {
      id: '6',
      title:
        'Include diverse participant recruitment plan reflecting US demographics',
      type: 'Explicit',
      sourceRef: 'PA-25-102, Section IV — Inclusion Requirements',
      artifact: 'Inclusion Enrollment Report',
      status: 'not-checked',
      notes:
        'Artifact not yet uploaded for AI review. Enrollment report must be submitted before compliance can be assessed.',
    },
    {
      id: '7',
      title:
        'Budget must not exceed $250K direct costs per year (modular format)',
      type: 'Explicit',
      sourceRef: 'SF424 R&R Application Guide, Section II.5.4',
      artifact: 'Modular Budget',
      status: 'not-checked',
      notes:
        'Budget artifact not yet submitted. Cannot verify cost limits until modular budget is uploaded.',
    },
    {
      id: '8',
      title: 'Address health disparities and impact on underserved populations',
      type: 'Implicit',
      sourceRef: 'NIA Strategic Plan 2026–2030, Priority Area 3',
      artifact: 'Overall',
      status: 'unknown',
      notes:
        'Significance section mentions health disparities briefly but may need stronger framing. Consider adding specific data on AD prevalence in underserved communities.',
    },
    {
      id: '9',
      title: 'Clearly differentiate innovation from prior R21 preliminary data',
      type: 'Implicit',
      sourceRef: 'Prior review feedback, Study Section CSR comments',
      artifact: 'Research Strategy',
      status: 'not-satisfied',
      notes:
        'Innovation section currently overlaps significantly with R21 preliminary data. Reviewers previously flagged this — needs clearer distinction of novel contributions.',
    },
    {
      id: '10',
      title:
        'PI must demonstrate relevant AD research expertise and track record',
      type: 'Explicit',
      sourceRef: 'PA-25-102, Section III — Eligibility, PI Qualifications',
      artifact: 'PI Biosketch',
      status: 'satisfied',
      notes:
        'PI biosketch documents 12 years of AD research, 47 peer-reviewed publications, and 3 prior NIA-funded projects.',
    },
    {
      id: '11',
      title: 'Data sharing plan must comply with 2023 NIH DMS Policy',
      type: 'Explicit',
      sourceRef: 'NOT-OD-23-015, NIH Data Management & Sharing Policy',
      artifact: 'DMS Plan',
      status: 'satisfied',
      notes:
        'DMS Plan references NACC and NDA repositories with a compliant 12-month sharing timeline. Reviewed by library data services.',
    },
    {
      id: '12',
      title: 'Letters of support must reference specific aims by name',
      type: 'Implicit',
      sourceRef: 'Best practices per NIH reviewer expectations',
      artifact: 'Letters of Support',
      status: 'not-satisfied',
      notes:
        '2 of 4 letters received. Dr. Martinez letter does not reference specific aims by name. Dr. Thompson letter pending.',
    },
    {
      id: '13',
      title: 'Demonstrate institutional commitment and adequate facilities',
      type: 'Implicit',
      sourceRef: 'PA-25-102, Section V — Review Criteria, Environment',
      artifact: 'Overall',
      status: 'satisfied',
      notes:
        'Facilities document highlights MRI suite, biobank, and computational cluster. Equipment list is current. Institutional support letter included.',
    },
  ],
  artifactCategories: [
    {
      id: 'personnel',
      label: 'Personnel',
      iconName: 'Users',
      completedCount: 3,
      totalCount: 5,
      artifacts: [
        {
          id: 'pi-biosketch',
          title: 'PI Biosketch — Dr. Sarah Chen',
          complianceStatus: 'satisfied',
          citation: '[SF424 §G.240]',
          requirementStatus: 'Required',
          considerations:
            "Highlight Alzheimer's-specific publications and prior NIA funding.",
          compliance: [
            { label: 'Page count (5/5 pages)', passed: true },
            { label: 'NIH format', passed: true },
            { label: 'SciENcv generated', passed: true },
          ],
          hasFile: true,
          readyStatus: true,
        },
        {
          id: 'coi-biosketch',
          title: 'Co-I Biosketch — Dr. James Park',
          complianceStatus: 'satisfied',
          citation: '[SF424 §G.240]',
          requirementStatus: 'Required',
          considerations: 'Include computational neuroscience expertise.',
          compliance: [
            { label: 'Page count (5/5 pages)', passed: true },
            { label: 'NIH format', passed: true },
            { label: 'SciENcv generated', passed: true },
          ],
          hasFile: true,
          readyStatus: true,
        },
        {
          id: 'key-personnel',
          title: 'Key Personnel Biosketches (3 remaining)',
          complianceStatus: 'not-satisfied',
          citation: '[SF424 §G.240]',
          requirementStatus: 'Required',
          considerations:
            'Ensure each biosketch highlights role-specific expertise.',
          compliance: [
            { label: 'Biosketches received (1/3)', passed: false },
            { label: 'NIH format', passed: true },
          ],
          hasFile: true,
          readyStatus: false,
        },
        {
          id: 'letters-support',
          title: 'Letters of Support',
          complianceStatus: 'not-satisfied',
          citation: '[RFP §4.7]',
          requirementStatus: 'Conditional',
          considerations: 'Each letter should reference specific aims.',
          compliance: [
            { label: 'Letters received (2/4)', passed: false },
            { label: 'All letters reference specific aims', passed: false },
            { label: 'Format compliant', passed: true },
          ],
          hasFile: true,
          readyStatus: false,
        },
        {
          id: 'current-pending',
          title: 'Current & Pending Support',
          complianceStatus: 'not-checked',
          citation: '[NOT-OD-21-073]',
          requirementStatus: 'Required',
          considerations: 'Must include all active and pending support.',
          compliance: [
            { label: 'SciENcv format', passed: false },
            { label: 'All personnel included', passed: false },
          ],
          hasFile: false,
          readyStatus: false,
        },
      ],
    },
    {
      id: 'research-plan',
      label: 'Research Plan',
      iconName: 'Beaker',
      completedCount: 2,
      totalCount: 6,
      artifacts: [
        {
          id: 'specific-aims',
          title: 'Specific Aims (1 page)',
          complianceStatus: 'satisfied',
          citation: '[SF424 §5.5.1]',
          requirementStatus: 'Required',
          considerations: 'Frame around NIA priority.',
          compliance: [
            { label: 'Page count (1/1)', passed: true },
            { label: 'Margins (0.5")', passed: true },
            { label: 'Font (Arial 11pt)', passed: true },
          ],
          hasFile: true,
          readyStatus: true,
        },
        {
          id: 'research-strategy',
          title: 'Research Strategy (12 pages)',
          complianceStatus: 'unknown',
          citation: '[SF424 §5.5.2]',
          requirementStatus: 'Required',
          considerations:
            'Significance section must address health disparities.',
          compliance: [
            { label: 'Page count (10/12 used)', passed: true },
            { label: 'Margins (0.5")', passed: true },
            {
              label: 'Innovation section distinct from R21 data',
              passed: false,
            },
            { label: 'Health disparities adequately addressed', passed: false },
          ],
          hasFile: true,
          readyStatus: false,
        },
        {
          id: 'bibliography',
          title: 'Bibliography & References Cited',
          complianceStatus: 'satisfied',
          citation: '[SF424 §5.5.5]',
          requirementStatus: 'Required',
          considerations: 'Include recent 2025–2026 publications.',
          compliance: [
            { label: 'No page limit', passed: true },
            { label: 'All citations formatted correctly', passed: true },
          ],
          hasFile: true,
          readyStatus: true,
        },
        {
          id: 'human-subjects',
          title: 'Protection of Human Subjects',
          complianceStatus: 'not-checked',
          citation: '[45 CFR 46]',
          requirementStatus: 'Conditional',
          considerations:
            'Address recruitment of underrepresented populations.',
          compliance: [
            { label: 'IRB approval', passed: false },
            { label: 'Inclusion plan', passed: false },
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'inclusion-enrollment',
          title: 'Inclusion Enrollment Report',
          complianceStatus: 'not-checked',
          citation: '[NOT-OD-18-116]',
          requirementStatus: 'Required',
          considerations: 'Target enrollment must reflect diversity goals.',
          compliance: [{ label: 'Form completed', passed: false }],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'dms-plan',
          title: 'Data Management & Sharing Plan',
          complianceStatus: 'satisfied',
          citation: '[NOT-OD-23-015]',
          requirementStatus: 'Required',
          considerations: 'Must comply with 2023 NIH DMS Policy.',
          compliance: [
            { label: 'DMS Policy compliant', passed: true },
            { label: 'Repository specified', passed: true },
          ],
          hasFile: true,
          readyStatus: true,
        },
      ],
    },
    {
      id: 'budget',
      label: 'Budget',
      iconName: 'DollarSign',
      completedCount: 0,
      totalCount: 3,
      artifacts: [
        {
          id: 'modular-budget',
          title: 'Modular Budget',
          complianceStatus: 'manual-review',
          citation: '[SF424 §II.5.4]',
          requirementStatus: 'Required',
          needsManualReview: true,
          considerations: 'Direct costs must not exceed $250K per year.',
          compliance: [],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'budget-justification',
          title: 'Budget Justification',
          complianceStatus: 'manual-review',
          citation: '[SF424 §II.5.4]',
          requirementStatus: 'Required',
          needsManualReview: true,
          considerations: 'Justify each budget category.',
          compliance: [],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'subcontract-budget',
          title: 'Subcontract Budget (UMich)',
          complianceStatus: 'manual-review',
          citation: '[SF424 §II.5.4]',
          requirementStatus: 'Conditional',
          needsManualReview: true,
          considerations: 'Confirm F&A rate agreement with UMich SPO.',
          compliance: [],
          hasFile: false,
          readyStatus: false,
        },
      ],
    },
    {
      id: 'supporting',
      label: 'Supporting Documents',
      iconName: 'FolderOpen',
      completedCount: 4,
      totalCount: 5,
      artifacts: [
        {
          id: 'facilities',
          title: 'Facilities & Resources',
          complianceStatus: 'satisfied',
          citation: '[SF424 §5.5.8]',
          requirementStatus: 'Required',
          considerations: 'Highlight MRI facility, biobank access.',
          compliance: [{ label: 'All facilities listed', passed: true }],
          hasFile: true,
          readyStatus: true,
        },
        {
          id: 'equipment',
          title: 'Equipment',
          complianceStatus: 'satisfied',
          citation: '[SF424 §5.5.9]',
          requirementStatus: 'Required',
          considerations: 'List major equipment available.',
          compliance: [{ label: 'Format compliant', passed: true }],
          hasFile: true,
          readyStatus: true,
        },
        {
          id: 'resource-sharing',
          title: 'Resource Sharing Plan',
          complianceStatus: 'satisfied',
          citation: '[SF424 §5.5.12]',
          requirementStatus: 'Required',
          considerations: 'Address model organism sharing.',
          compliance: [{ label: 'Sharing plan complete', passed: true }],
          hasFile: true,
          readyStatus: true,
        },
        {
          id: 'authentication',
          title: 'Authentication of Key Biological Resources',
          complianceStatus: 'satisfied',
          citation: '[NOT-OD-21-111]',
          requirementStatus: 'Conditional',
          considerations: 'Document authentication methods.',
          compliance: [{ label: 'Authentication documented', passed: true }],
          hasFile: true,
          readyStatus: true,
        },
        {
          id: 'consortium',
          title: 'Consortium/Contractual Arrangements',
          complianceStatus: 'unknown',
          citation: '[SF424 §5.5.13]',
          requirementStatus: 'Conditional',
          considerations: 'Must describe roles for UMich subcontract.',
          compliance: [
            { label: 'Agreement signed', passed: false },
            { label: 'Roles described', passed: true },
          ],
          hasFile: true,
          readyStatus: false,
        },
      ],
    },
  ],
  grantsmanshipSections: [
    {
      id: 'structure',
      name: 'Structure',
      iconName: 'LayoutList',
      summary:
        'The proposal follows a logical progression from molecular mechanisms to translational outcomes. Aim structure is well-organized with clear dependencies between the three aims. However, the Research Strategy would benefit from stronger transitions between Significance, Innovation, and Approach sections. The timeline figure also needs clearer go/no-go decision points to demonstrate contingency planning.',
      findings: [
        {
          id: 's1',
          text: 'Transition between Significance and Innovation sections is abrupt — consider a bridging paragraph that connects the knowledge gap to your novel approach',
          artifacts: ['Research Strategy'],
        },
        {
          id: 's2',
          text: 'Go/no-go decision points between aims are not clearly defined in the timeline figure — reviewers will want to see contingency planning',
          artifacts: ['Research Strategy'],
        },
        {
          id: 's3',
          text: 'Specific Aims page effectively sets up the rationale, but Aim 3 feels disconnected from Aims 1–2 — strengthen the logical thread',
          artifacts: ['Specific Aims', 'Research Strategy'],
        },
      ],
    },
    {
      id: 'narrative',
      name: 'Narrative',
      iconName: 'BookOpen',
      summary:
        "The scientific narrative is compelling and well-grounded in preliminary data. The opening of the Specific Aims page effectively establishes urgency around Alzheimer's disease burden. Key weakness: the Innovation section does not sufficiently differentiate this R01 from the prior R21, which was flagged in previous review. Health disparities framing needs significant expansion with specific prevalence data to meet NIA strategic priorities.",
      findings: [
        {
          id: 'n1',
          text: 'Innovation section overlaps significantly with R21 preliminary data — clearly delineate what is novel in this R01 versus what was established previously',
          artifacts: ['Research Strategy'],
        },
        {
          id: 'n2',
          text: 'Health disparities discussion is limited to one sentence in Significance — expand to 1–2 paragraphs with specific AD prevalence data for underserved populations',
          artifacts: ['Research Strategy'],
        },
        {
          id: 'n3',
          text: 'Consider leading the Specific Aims with projected 2060 AD prevalence figures to strengthen the epidemiological hook',
          artifacts: ['Specific Aims'],
        },
        {
          id: 'n4',
          text: 'Translational framing in Aim 3 connecting tau propagation to therapeutic targets is strong — ensure this thread carries through the Approach section',
          artifacts: ['Specific Aims', 'Research Strategy'],
        },
      ],
    },
    {
      id: 'clarity',
      name: 'Clarity',
      iconName: 'ScanEye',
      summary:
        'Writing is generally clear and accessible for the intended study section audience. Technical terminology is used appropriately and jargon is well-defined in most sections. Some inconsistencies in terminology across documents need attention, particularly around tau-related nomenclature. The statistical justification for the biomarker sub-study could be more explicit with a summary table of effect sizes.',
      findings: [
        {
          id: 'c1',
          text: "The term 'tau propagation' is used inconsistently — 'tau spreading' appears in Specific Aims while 'tau propagation' is used in Research Strategy. Standardize across all documents",
          artifacts: ['Specific Aims', 'Research Strategy'],
        },
        {
          id: 'c2',
          text: 'Statistical power analysis for the biomarker sub-study lacks a summary table — add detectable effect sizes at planned sample sizes for reviewer clarity',
          artifacts: ['Research Strategy'],
        },
        {
          id: 'c3',
          text: "Acronym 'CSF' is introduced in the Approach but used without definition in the Significance section earlier",
          artifacts: ['Research Strategy'],
        },
      ],
    },
  ],
  tasks: [
    {
      id: 't1',
      title: 'Upload Inclusion Enrollment Report',
      summary:
        'Enrollment report must be submitted before inclusion compliance can be assessed by AI review.',
      owner: 'Dr. Sarah Chen',
      ownerRole: 'PI',
      dueDate: 'Jan 10, 2027',
      status: 'open',
      source: 'ai',
      linkedType: 'artifact',
      linkedName: 'Inclusion Enrollment Report',
      isOverdue: false,
      isDueSoon: false,
    },
    {
      id: 't2',
      title: 'Collect letter of support — Dr. Thompson',
      summary: 'Letter must reference specific aims by name.',
      owner: 'Dr. Sarah Chen',
      ownerRole: 'PI',
      dueDate: 'Jan 3, 2027',
      status: 'open',
      source: 'email',
      linkedType: 'artifact',
      linkedName: 'Letters of Support',
      isOverdue: true,
      isDueSoon: false,
    },
    {
      id: 't3',
      title: 'Verify UMich subcontract F&A rate',
      summary:
        'Confirm negotiated F&A rate agreement with University of Michigan SPO.',
      owner: 'Maria Santos',
      ownerRole: 'Grants Admin',
      dueDate: 'Jan 5, 2027',
      status: 'open',
      source: 'manual',
      linkedType: 'artifact',
      linkedName: 'Subcontract Budget',
      isOverdue: true,
      isDueSoon: false,
    },
    {
      id: 't4',
      title: 'Expand health disparities framing',
      summary:
        'Significance section needs 1-2 paragraphs with specific AD prevalence data.',
      owner: 'Dr. Sarah Chen',
      ownerRole: 'PI',
      dueDate: 'Jan 15, 2027',
      status: 'open',
      source: 'ai',
      linkedType: 'criterion',
      linkedName: 'Health Disparities',
      isOverdue: false,
      isDueSoon: false,
    },
    {
      id: 't5',
      title: 'Finalize modular budget',
      summary:
        'Complete budget in modular format. Direct costs must not exceed $250K per year.',
      owner: 'Maria Santos',
      ownerRole: 'Grants Admin',
      dueDate: 'Jan 7, 2027',
      status: 'in-progress',
      source: 'manual',
      linkedType: 'artifact',
      linkedName: 'Modular Budget',
      isOverdue: false,
      isDueSoon: true,
    },
    {
      id: 't6',
      title: 'Revise Innovation section',
      summary:
        'Clearly differentiate R01 innovations from prior R21 preliminary data.',
      owner: 'Dr. Sarah Chen',
      ownerRole: 'PI',
      dueDate: 'Jan 18, 2027',
      status: 'in-progress',
      source: 'ai',
      linkedType: 'artifact',
      linkedName: 'Research Strategy',
      isOverdue: false,
      isDueSoon: false,
    },
    {
      id: 't7',
      title: 'Submit PI Biosketch',
      summary: 'Final biosketch uploaded and verified.',
      owner: 'Dr. Sarah Chen',
      ownerRole: 'PI',
      dueDate: 'Dec 20, 2026',
      status: 'complete',
      source: 'manual',
      linkedType: 'artifact',
      linkedName: 'PI Biosketch',
      isOverdue: false,
      isDueSoon: false,
    },
    {
      id: 't8',
      title: 'Upload Data Management & Sharing Plan',
      summary: 'DMS Plan submitted referencing NACC and NDA repositories.',
      owner: 'Dr. James Park',
      ownerRole: 'Co-I',
      dueDate: 'Dec 28, 2026',
      status: 'complete',
      source: 'email',
      linkedType: 'artifact',
      linkedName: 'DMS Plan',
      isOverdue: false,
      isDueSoon: false,
    },
  ],
}
